import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { supabase } from '../../lib/supabase';

const STATUS_COLORS = {
     new: 'bg-blue-500/20 text-blue-300',
     contacted: 'bg-orange-500/20 text-orange-300',
     qualified: 'bg-purple-500/20 text-purple-300',
     proposal_sent: 'bg-yellow/20 text-yellow',
     won: 'bg-green-500/20 text-green-300',
     lost: 'bg-red-500/20 text-red-300',
};

const STATUS_LABELS = {
     new: 'New',
     contacted: 'Contacted',
     qualified: 'Qualified',
     proposal_sent: 'Proposal Sent',
     won: 'Won',
     lost: 'Lost',
};

const PROPOSAL_STATUS_COLORS = {
     draft: 'bg-gray-50 text-darker/40',
     sent: 'bg-blue-500/20 text-blue-300',
     viewed: 'bg-orange-500/20 text-orange-300',
     accepted: 'bg-green-500/20 text-green-300',
     declined: 'bg-red-500/20 text-red-300',
     expired: 'bg-red-500/20 text-red-300',
};

const INVOICE_STATUS_COLORS = {
     draft: 'bg-gray-50 text-darker/40',
     sent: 'bg-blue-500/20 text-blue-300',
     viewed: 'bg-orange-500/20 text-orange-300',
     paid: 'bg-green-500/20 text-green-300',
     overdue: 'bg-red-500/20 text-red-300',
     cancelled: 'bg-red-500/20 text-red-300',
};

export default function InquiryDetail() {
     const { id } = useParams();
     const navigate = useNavigate();
     const [inquiry, setInquiry] = useState(null);
     const [proposals, setProposals] = useState([]);
     const [invoices, setInvoices] = useState([]);
     const [notes, setNotes] = useState('');
     const [loading, setLoading] = useState(true);
     const [saving, setSaving] = useState(false);

     useEffect(() => {
          fetchData();
          // eslint-disable-next-line react-hooks/exhaustive-deps
     }, [id]);

     async function fetchData() {
          const [inqRes, propRes, invRes] = await Promise.all([
               supabase.from('inquiries').select('*').eq('id', id).single(),
               supabase.from('proposals').select('*').eq('inquiry_id', id).order('created_at', { ascending: false }),
               supabase.from('invoices').select('*').eq('inquiry_id', id).order('created_at', { ascending: false }),
          ]);
          if (inqRes.data) {
               setInquiry(inqRes.data);
               setNotes(inqRes.data.notes || '');
          }
          setProposals(propRes.data || []);
          setInvoices(invRes.data || []);
          setLoading(false);
     }

     async function updateStatus(status) {
          await supabase
               .from('inquiries')
               .update({ status, updated_at: new Date().toISOString() })
               .eq('id', id);
          setInquiry({ ...inquiry, status });
     }

     async function saveNotes() {
          setSaving(true);
          await supabase
               .from('inquiries')
               .update({ notes, updated_at: new Date().toISOString() })
               .eq('id', id);
          setSaving(false);
     }

     const inputClasses =
          'bg-white border border-gray-200 rounded-lg px-4 py-2 text-darker placeholder-darker/30 focus:outline-none focus:border-yellow duration-300';

     if (loading) return <p className='text-darker/60'>Loading...</p>;
     if (!inquiry) return <p className='text-darker/60'>Inquiry not found.</p>;

     return (
          <div>
               {/* Header */}
               <div className='flex flex-col tablet:flex-row items-start tablet:items-center justify-between mb-6 gap-4'>
                    <div className='flex items-center gap-4'>
                         <button
                              onClick={() => navigate('/admin/inquiries')}
                              className='text-darker/40 hover:text-darker duration-300'
                         >
                              &larr; Back
                         </button>
                         <h1 className='text-darker text-2xl font-bold'>{inquiry.name}</h1>
                         <span
                              className={`px-2 py-1 rounded text-xs ${
                                   STATUS_COLORS[inquiry.status] || 'bg-gray-50 text-darker/40'
                              }`}
                         >
                              {STATUS_LABELS[inquiry.status] || inquiry.status}
                         </span>
                    </div>
               </div>

               {/* Status Actions */}
               <div className='flex flex-wrap gap-2 mb-6'>
                    {Object.entries(STATUS_LABELS).map(([key, label]) => (
                         <button
                              key={key}
                              onClick={() => updateStatus(key)}
                              disabled={inquiry.status === key}
                              className={`px-3 py-1.5 rounded-lg text-sm duration-300 ${
                                   inquiry.status === key
                                        ? 'bg-yellow/10 text-yellow cursor-default'
                                        : 'text-darker/60 hover:text-darker hover:bg-white border border-gray-200'
                              }`}
                         >
                              {label}
                         </button>
                    ))}
               </div>

               <div className='grid tablet:grid-cols-2 gap-6'>
                    {/* Contact Info */}
                    <div className='bg-white rounded-xl border border-gray-200 p-6'>
                         <h2 className='text-darker font-bold mb-4'>Contact Info</h2>
                         <div className='space-y-3 text-sm'>
                              <div>
                                   <p className='text-darker/40'>Name</p>
                                   <p className='text-darker'>{inquiry.name}</p>
                              </div>
                              <div>
                                   <p className='text-darker/40'>Email</p>
                                   <a href={`mailto:${inquiry.email}`} className='text-yellow hover:underline'>
                                        {inquiry.email}
                                   </a>
                              </div>
                              <div>
                                   <p className='text-darker/40'>Phone</p>
                                   <a href={`tel:${inquiry.phone}`} className='text-yellow hover:underline'>
                                        {inquiry.phone}
                                   </a>
                              </div>
                         </div>
                    </div>

                    {/* Project Details */}
                    <div className='bg-white rounded-xl border border-gray-200 p-6'>
                         <h2 className='text-darker font-bold mb-4'>Project Details</h2>
                         <div className='space-y-3 text-sm'>
                              <div>
                                   <p className='text-darker/40'>Timeline</p>
                                   <p className='text-darker'>{inquiry.project_timeline || 'Not specified'}</p>
                              </div>
                              <div>
                                   <p className='text-darker/40'>Description</p>
                                   <p className='text-darker'>{inquiry.project_description || 'Not provided'}</p>
                              </div>
                              <div>
                                   <p className='text-darker/40'>Submitted</p>
                                   <p className='text-darker'>
                                        {format(new Date(inquiry.created_at), 'MMM d, yyyy h:mm a')}
                                   </p>
                              </div>
                         </div>
                    </div>

                    {/* Source Tracking */}
                    <div className='bg-white rounded-xl border border-gray-200 p-6'>
                         <h2 className='text-darker font-bold mb-4'>Source Tracking</h2>
                         <div className='space-y-3 text-sm'>
                              {inquiry.gclid && (
                                   <div>
                                        <p className='text-darker/40'>GCLID</p>
                                        <p className='text-darker text-xs break-all'>{inquiry.gclid}</p>
                                   </div>
                              )}
                              {inquiry.utm_source && (
                                   <div>
                                        <p className='text-darker/40'>Source</p>
                                        <p className='text-darker'>{inquiry.utm_source}</p>
                                   </div>
                              )}
                              {inquiry.utm_medium && (
                                   <div>
                                        <p className='text-darker/40'>Medium</p>
                                        <p className='text-darker'>{inquiry.utm_medium}</p>
                                   </div>
                              )}
                              {inquiry.utm_campaign && (
                                   <div>
                                        <p className='text-darker/40'>Campaign</p>
                                        <p className='text-darker'>{inquiry.utm_campaign}</p>
                                   </div>
                              )}
                              {inquiry.source_url && (
                                   <div>
                                        <p className='text-darker/40'>Landing Page</p>
                                        <p className='text-darker text-xs break-all'>{inquiry.source_url}</p>
                                   </div>
                              )}
                              {inquiry.ip_address && (
                                   <div>
                                        <p className='text-darker/40'>IP Address</p>
                                        <p className='text-darker'>{inquiry.ip_address}</p>
                                   </div>
                              )}
                              {!inquiry.gclid && !inquiry.utm_source && (
                                   <p className='text-darker/40'>Direct visit (no tracking params)</p>
                              )}
                         </div>
                    </div>

                    {/* Notes */}
                    <div className='bg-white rounded-xl border border-gray-200 p-6'>
                         <h2 className='text-darker font-bold mb-4'>Notes</h2>
                         <textarea
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                              className={inputClasses + ' w-full h-32 resize-none'}
                              placeholder='Add notes about this lead...'
                         />
                         <button
                              onClick={saveNotes}
                              disabled={saving}
                              className='mt-2 px-4 py-2 bg-yellow text-black rounded-lg text-sm font-bold hover:bg-yellow/90 duration-300 disabled:opacity-50'
                         >
                              {saving ? 'Saving...' : 'Save Notes'}
                         </button>
                    </div>
               </div>

               {/* Proposals */}
               <div className='mt-6 bg-white rounded-xl border border-gray-200 p-6'>
                    <div className='flex items-center justify-between mb-4'>
                         <h2 className='text-darker font-bold'>Proposals</h2>
                         <Link
                              to={`/admin/inquiries/${id}/proposal/new`}
                              className='px-4 py-2 bg-yellow text-black rounded-lg text-sm font-bold hover:bg-yellow/90 duration-300'
                         >
                              Create Proposal
                         </Link>
                    </div>
                    {proposals.length === 0 ? (
                         <p className='text-darker/40 text-sm'>No proposals yet.</p>
                    ) : (
                         <div className='space-y-2'>
                              {proposals.map((p) => (
                                   <div
                                        key={p.id}
                                        className='flex items-center justify-between p-3 rounded-lg bg-gray-100'
                                   >
                                        <div>
                                             <span className='text-darker text-sm'>{p.proposal_number}</span>
                                             <span className='text-darker/40 text-sm ml-3'>
                                                  ${(p.total_cents / 100).toLocaleString()}
                                             </span>
                                        </div>
                                        <div className='flex items-center gap-3'>
                                             <span
                                                  className={`px-2 py-1 rounded text-xs ${
                                                       PROPOSAL_STATUS_COLORS[p.status] || 'bg-gray-50 text-darker/40'
                                                  }`}
                                             >
                                                  {p.status}
                                             </span>
                                             <Link
                                                  to={`/admin/proposals/${p.id}/edit`}
                                                  className='text-yellow text-sm hover:underline'
                                             >
                                                  Edit
                                             </Link>
                                        </div>
                                   </div>
                              ))}
                         </div>
                    )}
               </div>

               {/* Invoices */}
               <div className='mt-6 bg-white rounded-xl border border-gray-200 p-6'>
                    <div className='flex items-center justify-between mb-4'>
                         <h2 className='text-darker font-bold'>Invoices</h2>
                         <Link
                              to={`/admin/inquiries/${id}/invoice/new`}
                              className='px-4 py-2 bg-yellow text-black rounded-lg text-sm font-bold hover:bg-yellow/90 duration-300'
                         >
                              Create Invoice
                         </Link>
                    </div>
                    {invoices.length === 0 ? (
                         <p className='text-darker/40 text-sm'>No invoices yet.</p>
                    ) : (
                         <div className='space-y-2'>
                              {invoices.map((inv) => (
                                   <div
                                        key={inv.id}
                                        className='flex items-center justify-between p-3 rounded-lg bg-gray-100'
                                   >
                                        <div>
                                             <span className='text-darker text-sm'>{inv.invoice_number}</span>
                                             <span className='text-darker/40 text-sm ml-3'>
                                                  ${(inv.total_cents / 100).toLocaleString()}
                                             </span>
                                        </div>
                                        <div className='flex items-center gap-3'>
                                             <span
                                                  className={`px-2 py-1 rounded text-xs ${
                                                       INVOICE_STATUS_COLORS[inv.status] || 'bg-gray-50 text-darker/40'
                                                  }`}
                                             >
                                                  {inv.status}
                                             </span>
                                             <Link
                                                  to={`/admin/invoices/${inv.id}/edit`}
                                                  className='text-yellow text-sm hover:underline'
                                             >
                                                  Edit
                                             </Link>
                                        </div>
                                   </div>
                              ))}
                         </div>
                    )}
               </div>
          </div>
     );
}
